import React, { useState, useRef, useEffect } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

const CommonMultiSelect = React.forwardRef(({
  className,
  label,
  id,
  error,
  required = false,
  options = [],
  placeholder = "Select options",
  disableAutofill = false,
  loading = false,
  value = [],
  onChange,
  searchable = false,
  ...props
}, ref) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [open, setOpen] = useState(false);
  const searchInputRef = useRef(null);


  useEffect(() => {
    let filtered = options;


    if (searchable && searchTerm) {
      filtered = filtered.filter(option =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }


    if (Array.isArray(value) && value.length > 0) {
      filtered = filtered.filter(option => !value.includes(option.value));
    }

    setFilteredOptions(filtered);
  }, [searchTerm, options, searchable, value]);


  useEffect(() => {
    if (open && searchable && searchInputRef.current) {

      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [open, searchable]);


  const SearchInput = () => (
    <div className="p-1 border-b border-border">
      <Input
        ref={searchInputRef}
        type="text"
        placeholder="Search..."
        className="w-full text-sm focus-visible:ring-1 focus-visible:ring-primary"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        onClick={(e) => {
          e.stopPropagation();
        }}
        onKeyDown={(e) => {

          if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();

            if (filteredOptions.length === 1) {
              handleSelect(filteredOptions[0].value);
            }
          }

          if (e.key === 'Escape') {
            e.stopPropagation();
            setOpen(false);
          }
        }}

        onMouseDown={(e) => {
          e.stopPropagation();
        }}
      />
    </div>
  );


  const handleOpenChange = (isOpen) => {
    setOpen(isOpen);
    if (!isOpen) {
      setSearchTerm('');
    }
  };


  const handleSelect = (selectedValue) => {
    const newValue = Array.isArray(value)
      ? value.includes(selectedValue)
        ? value.filter(v => v !== selectedValue)
        : [...value, selectedValue]
      : [selectedValue];

    onChange(newValue);



  };


  const removeItem = (itemValue) => {
    const newValue = Array.isArray(value)
      ? value.filter(v => v !== itemValue)
      : [];

    onChange(newValue);
  };


  const getSelectedLabels = () => {
    if (!Array.isArray(value) || value.length === 0) return [];

    return value
      .map(val => options.find(opt => opt.value === val))
      .filter(opt => opt)
      .map(opt => opt.label);
  };

  const selectedLabels = getSelectedLabels();

  return (
    <div className="w-full">
      {label && (
        <Label
          htmlFor={id}
          className={`text-sm font-medium mb-1 inline-block transition-colors duration-200 ${error ? 'text-destructive' : 'text-foreground'
            }`}
        >
          {label} {required && <span className="text-destructive">*</span>}
        </Label>
      )}

      <div className="space-y-2">
        { }
        {selectedLabels.length > 0 && (
          <div className="flex flex-wrap gap-2 p-2 border rounded-md min-h-10">
            {selectedLabels.map((label, index) => (
              <Badge key={index} variant="secondary" className="flex items-center gap-1">
                {label}
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const itemValue = value[index];
                    removeItem(itemValue);
                  }}
                  className="ml-1 hover:bg-secondary-foreground/20 rounded-full p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}

        { }
        <Select
          value=""
          onValueChange={handleSelect}
          open={open}
          onOpenChange={handleOpenChange}
          disabled={loading}
          {...props}
        >
          <SelectTrigger
            id={id}
            className={cn(
              'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm',
              'ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium',
              'placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2',
              'focus-visible:ring-ring focus-visible:ring-offset-0 disabled:cursor-not-allowed',
              'disabled:opacity-50 transition-all duration-200',
              error ? 'border-destructive focus-visible:ring-destructive/30' : 'border-input focus-visible:ring-primary/30',
              className
            )}
          >
            <SelectValue>
              {selectedLabels.length > 0
                ? `${selectedLabels.length} item${selectedLabels.length !== 1 ? 's' : ''} selected`
                : (loading ? "Loading..." : placeholder)}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {searchable && <SearchInput />}
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (

                <SelectItem
                  key={option.value}
                  value={option.value !== undefined && option.value !== null && option.value !== '' ? option.value.toString() : `option-${option.label}`}
                >
                  {option.label}
                </SelectItem>
              ))
            ) : (

              <SelectItem value="__no_options__" disabled>
                No options available
              </SelectItem>
            )}
          </SelectContent>
        </Select>
      </div>

      {error && <p className="text-sm text-destructive mt-1 transition-opacity duration-200">{error}</p>}
    </div>
  );
});

CommonMultiSelect.displayName = 'CommonMultiSelect';

export { CommonMultiSelect };
